import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ServiceTypeMetadataMap, SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator } from '@common/enums';
import { DeleteArtifacts, ExportResultsOdata } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { SharedOdataService } from '@common/services';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { ExportResultsTag, ExportResultsTagsMap, SizeFilter } from '@common/services/smart-search';
import { FilterOptions } from '@common/types';
import { filterByWords } from '@common/utils';
import {
  containsWrapper,
  getFilterByContains,
  getFilterByDateRange,
  getFilterByEqFromEnum,
  getFilterBySize
} from '@common/utils/functions/search';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ExportResultsService extends SharedOdataService<ExportResultsOdata> {
  private readonly authContext = inject(AuthContextService);
  private readonly http = inject(HttpClient);

  readonly #dateFrom = ExportResultsTagsMap.get(ExportResultsTag.DateFrom);
  readonly #dateTo = ExportResultsTagsMap.get(ExportResultsTag.DateTo);
  readonly #fileName = ExportResultsTagsMap.get(ExportResultsTag.FileName);
  readonly #serviceType = ExportResultsTagsMap.get(ExportResultsTag.ServiceType);
  readonly #sizeMore = ExportResultsTagsMap.get(ExportResultsTag.SizeMore);
  readonly #sizeLess = ExportResultsTagsMap.get(ExportResultsTag.SizeLess);
  readonly #userName = ExportResultsTagsMap.get(ExportResultsTag.UserName);

  protected readonly authUserId = this.authContext.id;

  constructor() {
    super('ExportResults');
  }

  getExportResults(): Observable<ODataPagedResult<ExportResultsOdata>> {
    return this.userExportResults(this.authUserId());
  }

  private userExportResults(id: string): Observable<ODataPagedResult<ExportResultsOdata>> {
    const odataService = this.odataFactory.CreateService<ExportResultsOdata>(`/BackupUsers(${id})/Artifacts`);

    return this.fetchData<ExportResultsOdata>(odataService);
  }

  deleteSelected(Ids: string[]): Observable<boolean> {
    const payload: DeleteArtifacts = { Ids };

    return this.deleteArtifacts(payload);
  }

  private deleteArtifacts(payload: DeleteArtifacts): Observable<boolean> {
    const odataService = this.odataFactory.CreateService<DeleteArtifacts>(`/DeleteArtifacts`);

    return this.http.post<void>(odataService.Query().GetUrl(), payload).pipe(map(() => true));
  }

  updateFilter(searchObj: SmartSearchModel): void {
    const filter: string[] = [];

    if (searchObj[this.#userName.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#userName.tag] as SmartSearchModelField[],
        prop: this.#userName.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj[this.#fileName.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#fileName.tag] as SmartSearchModelField[],
        prop: this.#fileName.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj[this.#serviceType.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#serviceType.tag] as SmartSearchModelField[],
        prop: this.#serviceType.prop,
        enumItems: ServiceTypeMetadataMap
      };

      filter.push(getFilterByEqFromEnum(options));
    }

    if (searchObj[this.#dateFrom.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#dateFrom.tag] as SmartSearchModelField[],
        prop: this.#dateFrom.prop
      };
      const value = getFilterByDateRange(options, 'from');

      value && filter.push(value);
    }

    if (searchObj[this.#dateTo.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#dateTo.tag] as SmartSearchModelField[],
        prop: this.#dateTo.prop
      };
      const value = getFilterByDateRange(options, 'to');

      value && filter.push(value);
    }

    if (searchObj[this.#sizeMore.tag]) {
      const options: FilterOptions & { filter: SizeFilter } = {
        model: searchObj[this.#sizeMore.tag] as SmartSearchModelField[],
        prop: this.#sizeMore.prop,
        filter: SizeFilter.More
      };

      filter.push(getFilterBySize(options));
    }

    if (searchObj[this.#sizeLess.tag]) {
      const options: FilterOptions & { filter: SizeFilter } = {
        model: searchObj[this.#sizeLess.tag] as SmartSearchModelField[],
        prop: this.#sizeLess.prop,
        filter: SizeFilter.Less
      };

      filter.push(getFilterBySize(options));
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');
      const keys = [this.#userName.prop, this.#fileName.prop];
      const values = keys.map((k) => `(${containsWrapper(k, term)})`);

      filter.push('('.concat(values.join(` ${BooleanOperator.Or} `), ')'));
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }
}
